import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { listingRefUrl, photoRefUrl } from "../../helpers/simpleMarkdown";
import { emptyPropertyPlaceholderImages } from "@/constants/propertyPlaceholderImages";

// Same "no real photo yet" treatment /my-inventory already gives a listing
// row - a random stock house image, not a broken-image icon - so an
// inventory card here doesn't look worse than the list it was pulled from.
// Picked fresh per render, matching that page's own behavior.
const randomInventoryPlaceholder = () => {
  const name =
    emptyPropertyPlaceholderImages[
      Math.floor(Math.random() * emptyPropertyPlaceholderImages.length)
    ];
  return require(`@/images/${name}`);
};

// Label/class per lifecycle status, one map per card type since inventory
// and want-listings don't share a status vocabulary (InventoryRead's four
// booleans vs. gitcha-mcp's status_for() states). An unrecognized status (a
// future flag combination the model wasn't told about) falls back to the
// "published" treatment rather than hiding the card - the card itself is
// still a real, valid listing reference either way.
const INVENTORY_STATUS_META = {
  draft: { label: "Draft", className: "is-draft" },
  published: { label: "Published", className: "is-published" },
  sold: { label: "Sold", className: "is-sold" },
  archived: { label: "Archived", className: "is-archived" },
};

// pending_with_contigencies isn't in this set - the prompt collapses it to
// "pending" for card display, same "simplify for the badge, don't invent a
// fifth pending-ish label" call as everywhere else the model curates.
const LISTING_STATUS_META = {
  draft: { label: "Draft", className: "is-draft" },
  published: { label: "Published", className: "is-published" },
  pending: { label: "Pending", className: "is-pending" },
  closed: { label: "Closed", className: "is-sold" },
  terminated: { label: "Terminated", className: "is-archived" },
  archived: { label: "Archived", className: "is-archived" },
};

// An active mls listing reuses the "published" treatment (not a new
// className): on-market, the same visual weight as a published inventory/want
// listing. pending/closed/terminated (gitcha-mcp maps the feed's Canceled/
// Withdrawn/Expired to "terminated") reuse the want-listing badge classes.
const MLS_STATUS_META = {
  active: { label: "Active", className: "is-published" },
  pending: { label: "Pending", className: "is-pending" },
  closed: { label: "Closed", className: "is-sold" },
  terminated: { label: "Terminated", className: "is-archived" },
};

const STATUS_META_BY_TYPE = {
  inventory: INVENTORY_STATUS_META,
  listing: LISTING_STATUS_META,
  mls: MLS_STATUS_META,
};

// One row in a card-group: thumbnail on the left, details on the right - the
// layout explicitly asked for over the feed's stacked-thumbnail card, since
// the chat panel is a narrow column where several results need to read as a
// scannable list, not a row of large cards.
//
// `selectable`/`isSelected`/`onSelect`/`locked` only apply to a card-group
// the model marked with `[[choice]]` (a "which one?" prompt) - an ordinary
// results list still just links out, unchanged. `locked` is true once any
// card in the group has been picked: the whole group freezes at that point
// (radio state stays visible, nothing is clickable anymore) rather than
// letting the agent silently change their answer after Copilot has already
// moved on to acting on the first choice.
const ListingCard = ({ card, selectable, isSelected, onSelect, locked, openInPlace, disableLinks }) => {
  const statusMeta = STATUS_META_BY_TYPE[card.refType] || INVENTORY_STATUS_META;
  const meta = statusMeta[card.status] || statusMeta.published || statusMeta.active;
  // A draft want-listing routes to the edit flow, not the (nonexistent, for
  // an unpublished ad) detail page - the same distinction listingRefUrl
  // already draws for inline `[[listing:...]]`/`[[draft:...]]` markers.
  const linkRefType =
    card.refType === "listing" && card.status === "draft"
      ? "draft"
      : card.refType;
  const url = listingRefUrl({ refType: linkRefType, refId: card.refId });
  // "none" is the literal sentinel the model sends for a photo-less listing
  // (most chat-created drafts) - photoRefUrl's own host allowlist rejects it
  // (and anything else malformed) the same way it rejects any other bad URL,
  // so this never needs a separate "is this the sentinel" branch.
  const photoUrl = photoRefUrl({ url: card.photo });
  // photoRefUrl only validates the URL's shape/host - it says nothing about
  // whether that S3 object actually exists yet (a map render job that
  // hasn't run for this listing, or any other 404/network failure). Without
  // this, a well-formed but broken URL fell all the way through to the
  // browser's own broken-image icon with the title bleeding through it -
  // exactly the "broken reference" experience this file's photoRefUrl/
  // listingRefUrl already go out of their way to avoid elsewhere.
  const [imgFailed, setImgFailed] = useState(false);
  // A want-listing's thumbnail is a map, not a photo, and an mls listing
  // that's genuinely missing its photo shouldn't borrow a random unrelated
  // one either (same "would misrepresent it as a specific property"
  // reasoning as the want-listing case) - both keep the map-pin icon
  // rather than inventory's random-stock-photo fallback.
  const usesMapPinFallback = card.refType === "listing" || card.refType === "mls";
  // Picked once per distinct card, not once per render - this component's
  // parent re-renders on every coalesced animation frame while a reply is
  // streaming in, so a random pick made directly in the render body cycled
  // a photo-less card through different stock house images ~60 times a
  // second while the text streamed (a bug caught in review). Keyed on
  // refType+refId (not refId alone - two different card types can share a
  // numeric id, e.g. inventory:1 and listing:1) so it's re-picked only when
  // the card itself actually changes, not on every re-render of the same
  // card.
  const placeholderImage = useMemo(
    () => randomInventoryPlaceholder(),
    [card.refType, card.refId],
  );
  // price/beds/baths can each be empty - a want-listing's budget/bedrooms/
  // bathrooms criteria are all optional, so a real listing can genuinely
  // have none of them set. Drop whichever facts are missing instead of
  // rendering "· 3 bed ·" with a dangling separator either side.
  const facts = [
    card.price,
    card.beds && `${card.beds} bed`,
    card.baths && `${card.baths} bath`,
  ]
    .filter(Boolean)
    .join(" · ");

  const body = (
    <>
      {selectable && (
        <span
          className={`chat-listing-card__radio${isSelected ? " is-selected" : ""}`}
          aria-hidden="true"
        />
      )}
      <div className={`chat-listing-card__thumb ${meta.className}`}>
        {photoUrl && !imgFailed ? (
          <img
            src={photoUrl}
            alt={card.title}
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : usesMapPinFallback ? (
          <MapPin size={18} aria-hidden="true" />
        ) : (
          <img
            src={placeholderImage}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
        )}
      </div>
      <div className="chat-listing-card__body">
        <div className="chat-listing-card__title-row">
          <span className="chat-listing-card__title">{card.title}</span>
          <span className={`chat-listing-card__badge ${meta.className}`}>
            {meta.label}
          </span>
        </div>
        {facts && <div className="chat-listing-card__facts">{facts}</div>}
        <div className="chat-listing-card__city">{card.city}</div>
        {card.agent && (
          // Required listing-agent/brokerage attribution for MLS data
          // display (see the compliance note in simpleMarkdown.js's
          // parseCardLine) - not decorative, never hidden or truncated
          // away even on a narrow card.
          <div className="chat-listing-card__agent">{card.agent}</div>
        )}
      </div>
    </>
  );

  const className = `chat-listing-card ${meta.className}`;

  // A choice card never links out - clicking it answers the question
  // instead of navigating away, same as picking an option in any other
  // form. Locked (an answer was already given) makes it fully inert: no
  // hover state, no click, just the frozen radio result.
  //
  // Also disabled while `disableLinks` (streaming) - onSelect ultimately
  // calls send(), which HelpChatPanel itself refuses while a turn is still
  // in flight. Without this, clicking a card mid-reply set `locked` via
  // ListingCardList's own state (freezing the whole group) while the send
  // was silently dropped underneath it - a permanently frozen radio group
  // that never got an answer (a bug caught in review).
  if (selectable) {
    return (
      <button
        type="button"
        className={`${className} chat-listing-card--choice${isSelected ? " is-selected" : ""}`}
        onClick={() => onSelect(card)}
        disabled={locked || disableLinks}
        role="radio"
        aria-checked={isSelected}
      >
        {body}
      </button>
    );
  }

  // No detail page to link to (an unknown refType, or a missing id) still
  // shows the card - same "degrade, never hide or break" rule the inline
  // markers already follow - just as a non-interactive row.
  // On the full chat page, a card navigates in place: the conversation is a
  // saved, addressable page, so Back returns the agent to exactly the reply
  // they clicked from, and a chat that is a place to act from shouldn't
  // scatter a tab every time they follow something up.
  //
  // In the docked panel it still opens a new tab: that panel rides on top of
  // whatever page the agent is working on, so navigating in place would take
  // away the work they opened the panel to ask about. noopener/noreferrer is
  // the standard target="_blank" hardening - the new tab must not get a
  // handle back to this window.
  //
  // disableLinks (true while any turn is streaming) turns an in-place link
  // inert rather than navigating: the panel aborts its in-flight request on
  // unmount (see HelpChatPanel), and an in-place navigation away from the
  // chat page unmounts it - so without this, clicking a card mid-reply would
  // silently kill that reply. The docked panel's target="_blank" never
  // unmounts anything, so it has no such hazard and stays clickable.
  if (url && disableLinks && openInPlace) {
    return (
      <div className={`${className} chat-listing-card--disabled`} aria-disabled="true">
        {body}
      </div>
    );
  }

  return url ? (
    <Link
      to={url}
      target={openInPlace ? undefined : "_blank"}
      rel={openInPlace ? undefined : "noopener noreferrer"}
      className={className}
    >
      {body}
    </Link>
  ) : (
    <div className={`${className} chat-listing-card--static`}>{body}</div>
  );
};

export const ListingCardList = ({ items, selectable, onSelect, openInPlace, disableLinks }) => {
  const [selectedIndex, setSelectedIndex] = React.useState(null);

  const handleSelect = (card, index) => {
    if (selectedIndex !== null) return;
    setSelectedIndex(index);
    onSelect(card);
  };

  return (
    <div
      className={`chat-listing-card-list${selectable ? " is-choice" : ""}`}
      role={selectable ? "radiogroup" : undefined}
    >
      {items.map((card, i) => (
        <ListingCard
          key={`${card.refType}-${card.refId}-${i}`}
          card={card}
          selectable={selectable}
          isSelected={selectedIndex === i}
          locked={selectedIndex !== null}
          onSelect={(c) => handleSelect(c, i)}
          openInPlace={openInPlace}
          disableLinks={disableLinks}
        />
      ))}
    </div>
  );
};

export default ListingCard;
